import {
  css, Global,
} from '@emotion/react';
import {
  useCallback, useEffect, useState,
} from 'react';
import { RegularDropdownComponent } from '~/_shared/baseComponents/dropdown';
import { GoogleMapComponent } from '~/_shared/components/google-map/google-map.component';
import type { GoogleMapInstance } from '~/_shared/components/google-map/useGetGoogleMapInstance.hook';
import type { Theme } from '~/_shared/themes/theme.model';
import { ReportMapSummaryComponent } from '~/report/mapSummary/reportMapSummary.component';
import type { ReportTemplate } from '~/report/template/reportTemplate';
import businessProfileTemplate from '../../../report/businessProfileTemplate.rt.yml';
import cexTemplate from '../../../report/consumerExpenditureSummaryTemplate.rt.yml';

type MapPdfSummaryProps = Readonly<{
  className?: string;
}>;

const tableStyle = css({
  width: '100%',
  borderSpacing: 0,
});

const tableHeaderSpacerStyle = css({
  height: 5,
});

const tableHeaderCellStyle = css({
  background: '#EAF5FB',
  border: '0 solid #DFE3E8',
  padding: 5,
  textAlign: 'right',
  borderWidth: '1px 0',
  boxShadow: 'rgba(0,0,0, 8%) 0 1px 0',
  position: 'relative',
  '&:first-of-type': {
    borderRadius: '8px 0 0 8px',
    borderLeftWidth: 1,
    textAlign: 'left',
  },
  '&:last-of-type': {
    borderRadius: '0 8px 8px 0',
    borderRightWidth: 1,
  },
});

// width in px to take entire page width is 1020px or 315mm
const globalStyle = css`
  @page {
    size: A4 portrait;
    margin: 2mm;
    background: #fff;
  }
  body {
    background: #ffffff;
    color: #2c3e50;
  }
  #root {
    overflow: visible !important;
  }
`;

const printHiddenStyle = css`
  @media print {
    display: none;
  }
`;

const scaleMmToFitPage = (size: number) => `${270 / 210 * size}mm`;

const wrapperStyle = css({
  position: 'relative',
  width: scaleMmToFitPage(190),
  margin: '0 auto',
  boxSizing: 'border-box',
  paddingBottom: 20,
});

const mapsWrapperStyle = css({
  width: '100%',
  height: 300,
  breakInside: 'avoid',
  display: 'flex',
  gap: 10,
  justifyContent: 'space-between',
  marginTop: 10,
});

const templateSelectorTextStyle = css({
  margin: 0,
});

const templateDropdownStyle = css({
  width: 350,
  marginBottom: 20,
});

const tableTitleStyle = css({
  display: 'block',
  fontSize: 18,
  fontWeight: 700,
  textTransform: 'uppercase',
  margin: '20px 0 8px',
});

const rowStyle = ({ isEven }: { isEven: boolean }) => (theme: Theme) => css({
  background: isEven ? '#F5F9FB' : '#fff',
  borderBottom: `1px solid ${theme.borderColors.primary}`,
  breakInside: 'avoid',
});

const tableDataCellStyle = ({ isFirstRow, isLastRow }: {isFirstRow: boolean; isLastRow: boolean}) => css({
  textAlign: 'right',
  border: '0 solid #DFE3E8',
  padding: 5,
  borderTopWidth: isFirstRow ? 1 : 0,
  borderBottomWidth: isLastRow ? 1 : 0,
  '&:first-of-type': {
    borderLeftWidth: 1,
    textAlign: 'left',
    ...isFirstRow ? {
      borderRadius: '8px 0 0 0',
    } : {},
    ...isLastRow ? {
      borderRadius: '0 0 0 8px',
    } : {},
  },
  '&:last-of-type': {
    borderRightWidth: 1,
    ...isFirstRow ? {
      borderRadius: '0 8px 0 0',
    } : {},
    ...isLastRow ? {
      borderRadius: '0 0 8px 0',
    } : {},
  },
});

const pageBreakStyle = css({
  breakAfter: 'page',
});

const TableTitleRowComponent = ({ title, colSpan }: {title: string; colSpan: number}) => (
  <tr>
    <td colSpan={colSpan} css={{ padding: 5, fontWeight: 700, fontSize: 16, textTransform: 'uppercase', border: '0 solid #DFE3E8', borderWidth: '0 1px' }}>{title}</td>
  </tr>
);

const reportTemplates = [{
  id: 'bp',
  name: 'Business Profile',
  src: businessProfileTemplate,
}, {
  id: 'cex',
  name: 'Consumer Expenditure Summary',
  src: cexTemplate,
}];

export const MapPdfSummaryComponent = (_props: MapPdfSummaryProps) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState('bp');
  const [map, setMap] = useState<GoogleMapInstance>();
  const [map2, setMap2] = useState<GoogleMapInstance>();

  const template: ReportTemplate | null = reportTemplates
    .find(item => item.id === selectedTemplateId)?.src ?? null;

  const handleMapInit = useCallback((map: GoogleMapInstance) => {
    setMap(map);

    setTimeout(() => {
      (window as any).isReportFullyLoaded = true;
    }, 1000);
  }, []);

  const handleMapInit2 = useCallback((map: GoogleMapInstance) => {
    setMap2(map);

    setTimeout(() => {
      (window as any).isReportFullyLoaded = true;
    }, 1000);
  }, []);

  useEffect(() => {
    if (!map) {
      return;
    }

    map.instance.setOptions({
      center: {
        lat: 28.3366342,
        lng: -81.6377178,
      },
      zoom: 15,
      disableDefaultUI: true,
    });
  }, [map]);

  useEffect(() => {
    if (!map2) {
      return;
    }

    map2.instance.setOptions({
      center: {
        lat: 28.3366342,
        lng: -81.6377178,
      },
      mapTypeId: 'hybrid',
      zoom: 15,
      disableDefaultUI: true,
    });
  }, [map2]);

  const tableColumnsCount = 4;

  return (
    <div
      css={wrapperStyle}
    >
      <Global styles={globalStyle} />

      <div css={printHiddenStyle}>
        <p css={templateSelectorTextStyle}>Select Template</p>
        <div css={templateDropdownStyle}>
          <RegularDropdownComponent
            value={selectedTemplateId}
            onChange={setSelectedTemplateId}
            options={reportTemplates.map(item => ({ value: item.id, name: item.name }))}
          />
        </div>
      </div>

      {template && (
        <>
          <ReportMapSummaryComponent heading={template.name} latLng={{ lat: 40.290, lng: 50.223 }} />

          {template.items.map((templateItem, index) => {
            if (templateItem.type === 'map') {
              return (
                <div key={`maps-${index}`} css={mapsWrapperStyle}>
                  <GoogleMapComponent onMapInitialized={handleMapInit} id="map1" />
                  <GoogleMapComponent onMapInitialized={handleMapInit2} id="map2" />
                </div>
              );
            }

            if (templateItem.type === 'page-break') {
              return (
                <div key={`page-break-${index}`} css={pageBreakStyle} />
              );
            }

            if (templateItem.type === 'line-break') {
              return (
                <div key={`line-break-${index}`}>
                  <br />
                </div>
              );
            }

            if (templateItem.type === 'title') {
              return (
                <div key={`title-${index}`} css={tableTitleStyle}>{templateItem.content}</div>
              );
            }

            if (templateItem.type === 'table') {
              return (
                <table key={`table-${index}`} css={tableStyle}>
                  <thead>
                    <tr>
                      <td css={tableHeaderCellStyle}>{templateItem.title}</td>
                      <td css={tableHeaderCellStyle}>
                        <div><strong>5km</strong></div>
                        Radius Circle
                      </td>
                      <td css={tableHeaderCellStyle}>
                        <div><strong>10km</strong></div>
                        Radius Circle
                      </td>
                      <td css={tableHeaderCellStyle}>
                        <div><strong>15km</strong></div>
                        Radius Circle
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={tableColumnsCount} css={tableHeaderSpacerStyle} />
                    </tr>
                  </thead>

                  <tbody>
                    {templateItem.content.map((templateRow, index) => {
                      const count = templateItem.content.length;

                      if (templateRow.type === 'data-row') {
                        return (
                          <tr css={rowStyle({ isEven: index % 2 === 0 })} key={`data-row${index}`}>
                            <td css={tableDataCellStyle({ isFirstRow: index === 0, isLastRow: index === count - 1 })}>
                              Label: {templateRow.id}
                            </td>
                            <td css={tableDataCellStyle({ isFirstRow: index === 0, isLastRow: index === count - 1 })}>
                              Value 1: {templateRow.id}
                            </td>
                            <td css={tableDataCellStyle({ isFirstRow: index === 0, isLastRow: index === count - 1 })}>
                              Value 2: {templateRow.id}
                            </td>
                            <td css={tableDataCellStyle({ isFirstRow: index === 0, isLastRow: index === count - 1 })}>
                              Value 3: {templateRow.id}
                            </td>
                          </tr>
                        );
                      }

                      if (templateRow.type === 'title-row') {
                        return (
                          <TableTitleRowComponent
                            key={`title-row-${index}`}
                            title={templateRow.content}
                            colSpan={tableColumnsCount}
                          />
                        );
                      }

                      return null;
                    })}
                  </tbody>
                </table>
              );
            }

            return null;
          })}
        </>
      )}
    </div>
  );
};
